import React, { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { translate } from 'services/i18nService';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';

import { FeedsCarouselStream, FeedsCarouselViewport } from './types';
import FeedsCarouselItem from './Item';
import styles from './index.css';


type Props = {
  streams: FeedsCarouselStream[]
}

const FeedsCarousel: FC<Props> = (props) => {
  const { streams } = props;

  const { isEventCarouselOpen } = UserStore;
  const { realityShow } = ProfileStore;
  const isMainEvent: boolean = Boolean(realityShow?.mainEvent);

  const container = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState({ top: false, bottom: true });
  const [viewport, setViewport] = useState<FeedsCarouselViewport>({
    scroll: 0,
    height: 0,
    scrollHeight: 0,
  });

  const onScroll = useCallback(() => {
    if (!container.current) {
      return;
    }

    const { scrollTop, scrollHeight, offsetHeight } = container.current;

    setViewport({
      scroll: scrollTop,
      height: offsetHeight,
      scrollHeight,
    });

    setCanScroll({
      top: scrollTop > 0,
      bottom: scrollHeight - scrollTop > offsetHeight,
    });
  }, []);

  useEffect(() => {
    if (!streams?.length || !container.current) {
      return;
    }

    const { scrollTop, scrollHeight, offsetHeight } = container.current;

    setViewport({
      scroll: scrollTop,
      height: offsetHeight,
      scrollHeight,
    });
  }, [streams]);

  const feedElement = useMemo(() => {
    if (!streams.length) {
      return (
        <p className={styles.emptyText}>
          {translate('v2profile.carousel.empty')}
        </p>
      );
    }

    const renderItem = (stream: FeedsCarouselStream) => (
      <FeedsCarouselItem
        key={stream.username}
        isVisible={isEventCarouselOpen}
        stream={stream}
        viewport={viewport}
      />
    );

    return (
      <Fragment>
        {streams.map(renderItem)}
      </Fragment>
    );
  }, [isEventCarouselOpen, streams, viewport]);

  if (!isMainEvent) {
    return null;
  }

  const className = {
    [styles.container]: true,
    [styles.canScrollTop]: canScroll.top,
    [styles.canScrollBottom]: canScroll.bottom,
  };

  return (
    <div
      ref={container}
      className={classNames(className)}
      onScroll={onScroll}
    >
      {feedElement}
    </div>
  );
};

export default observer(FeedsCarousel);
